import type { FetchRequest } from '@jet/environment';
import { isSome } from '@jet/environment/types/optional';
import { inject } from '@jet/environment/dependencies/object-graph';
import type { ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '../dependencies/object-graph-type';
import type { ClassicalAPIResponse } from '../api/types';
import { NetworkError } from '../api/network-error';
import { isNonSubscriber } from '../dependencies/subscription-status-coordinator';

export function requestClassicalAPIResource(
    request: FetchRequest,
    objectGraph: ObjectGraph,
    allowEmptySections?: boolean,
): Promise<ClassicalAPIResponse> {
    const net = inject(ObjectGraphType.network, objectGraph);

    // Classical API Requires us to pass this explicit header for Non Subscribers to render their options similar to Non Signed In Users
    if (isNonSubscriber(objectGraph)) {
        request.headers = { ...request.headers, 'x-is-auth': 'false' };
    }

    return net.fetch(request).then((response) => {
        try {
            if (response.status === 404) {
                // item is not available in this storefront, or perhaps at all
                throw NetworkError.noContent();
            } else if (!response.ok) {
                throw new NetworkError(
                    response.status,
                    `Bad Status code [${response.status}] for [${request.url}]`,
                );
            }

            const body = response.body;
            const hasBody = isSome(body) && body.length > 0;

            // Media API POST/DELETE endpoints return an empty response body
            // Treating this the same as an empty data object.
            if (!hasBody && request.method === 'GET') {
                throw NetworkError.noContent();
            }

            const metrics = response.metrics;

            const parsedBody = hasBody ? JSON.parse(body) : null;

            // If we have an empty sections object, throw a 204 (No Content).
            if (
                Array.isArray(parsedBody['sections']) &&
                parsedBody['sections'].length === 0 &&
                !allowEmptySections
            ) {
                throw NetworkError.noContent();
            }

            return {
                body: parsedBody,
                statusCode: response.status,
                metrics,
            };
        } catch (e) {
            if (e instanceof NetworkError) {
                throw e;
            }
            console.error(e);
            throw new Error(`Error Fetching: ${request.url}, ${e}`);
        }
    });
}
